import React from 'react';
import { RECOGNITION_CELL_LABELS, RECOGNITION_CELL_ORDER } from '../../constants/recognitionReview';
import type { RecognitionOutcomeCell, RecognitionOutcomeCellCounts } from '../../types';

/**
 * The six-cell Recognition confidence outcome distribution as one stacked bar.
 *
 * Hue carries the match state (correct / adjusted / incorrect) and saturation carries the
 * confidence verdict, so a healthy week reads as one solid green band. Zero-count cells are
 * omitted entirely rather than rendered as hairlines.
 */
export const OutcomeDistributionBar: React.FC<{
  cells: RecognitionOutcomeCellCounts;
  /** Denominator override; defaults to the sum of `cells`. */
  total?: number;
  height?: number;
  ariaLabel?: string;
  onSegmentClick?: (cell: RecognitionOutcomeCell) => void;
}> = ({
  cells,
  total,
  height = 12,
  ariaLabel = 'Recognition outcome distribution',
  onSegmentClick,
}) => {
  const countedTotal = RECOGNITION_CELL_ORDER.reduce((sum, cell) => sum + (cells[cell] ?? 0), 0);
  const denominator = total ?? countedTotal;

  if (denominator <= 0) {
    return (
      <div
        className="rr-outcome-bar rr-outcome-bar--empty"
        style={{ height }}
        aria-label={`${ariaLabel}: no reviewed samples`}
        role="img"
      />
    );
  }

  return (
    <div
      className="rr-outcome-bar"
      style={{ height }}
      aria-label={ariaLabel}
      // Without a click handler every segment is a plain `div`, so the bar is one labelled graphic
      // rather than a group of controls - the same role the empty state already takes.
      role={onSegmentClick ? 'group' : 'img'}
    >
      {RECOGNITION_CELL_ORDER.map((cell) => {
        const count = cells[cell] ?? 0;
        if (count <= 0) return null;
        const share = (count / denominator) * 100;
        const label = RECOGNITION_CELL_LABELS[cell];
        const title = `${label}: ${count} (${share.toFixed(1)}%)`;
        const className = `rr-outcome-bar-segment is-${cell.replace(/_/g, '-')}`;

        if (!onSegmentClick) {
          return (
            <div
              key={cell}
              className={className}
              data-testid={`outcome-segment-${cell}`}
              style={{ width: `${share}%` }}
              title={title}
            />
          );
        }
        return (
          <button
            key={cell}
            type="button"
            className={`${className} is-clickable`}
            data-testid={`outcome-segment-${cell}`}
            style={{ width: `${share}%` }}
            title={title}
            aria-label={title}
            onClick={() => onSegmentClick(cell)}
          />
        );
      })}
    </div>
  );
};

export default OutcomeDistributionBar;
